/**
 * Admin System API — health, queues, cron status, active alerts.
 * Maps to backend: GET /admin/system/{health,queues,cron,alerts}
 */

import { apiFetch } from "./client";

// ─── Response Types ───

type HealthStatus = {
  readonly status: "healthy" | "degraded" | "down";
  readonly latency_ms?: number;
  readonly error?: string;
};

export type ComponentHealth = {
  readonly api: HealthStatus;
  readonly neon: HealthStatus;
  readonly kv: HealthStatus;
  readonly r2: HealthStatus;
};

export type HealthResult = {
  readonly components: ComponentHealth;
  readonly checked_at: string;
};

export type QueueStats = {
  readonly poison_message_count: number;
  readonly recent_job_stats: {
    readonly pending: number;
    readonly running: number;
    readonly failed_24h: number;
  };
};

export type CronTaskStatus = {
  readonly name: string;
  readonly status: "ok" | "warning" | "unknown";
  readonly detail: string;
};

export type CronResult = {
  readonly tasks: ReadonlyArray<CronTaskStatus>;
};

export type SystemAlertLevel = "P1" | "P2" | "P3";

export type SystemAlertRunbook = {
  readonly owner: string;
  readonly first_action: string;
  readonly drilldown: string;
  readonly can_retry: boolean;
  readonly audit_required: boolean;
};

export type SystemAlert = {
  readonly id: string;
  readonly level: SystemAlertLevel;
  readonly title: string;
  readonly detail: string;
  readonly drilldown_url: string;
  readonly created_at: string;
  readonly runbook: SystemAlertRunbook | null;
};

export type SystemAlertsResult = {
  readonly alerts: ReadonlyArray<SystemAlert>;
  readonly checked_at: string;
};

// ─── API Calls ───

export async function fetchSystemHealth(): Promise<HealthResult> {
  const res = await apiFetch<{ data: HealthResult }>("/admin/system/health");
  return res.data;
}

export async function fetchQueueStats(): Promise<QueueStats> {
  const res = await apiFetch<{ data: QueueStats }>("/admin/system/queues");
  return res.data;
}

export async function fetchCronStatus(): Promise<CronResult> {
  const res = await apiFetch<{ data: CronResult }>("/admin/system/cron");
  return res.data;
}

export async function fetchSystemAlerts(): Promise<SystemAlertsResult> {
  const res = await apiFetch<{ data: SystemAlertsResult }>(
    "/admin/system/alerts",
  );
  return res.data;
}
